import type { ReactNode } from 'react';
import { cn, formatNumber } from '@/lib/utils';
import { RefreshCw, Info, Timer, Users, Camera, Repeat2 } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
    useProductSessionAnalytics,
    formatDurationSec,
    formatHoursToHuman,
    type ProductCaptureMilestone,
    type ProductSessionOverview,
} from '@/hooks/useProductSessionAnalytics';
import { pctPointDelta } from '@/hooks/useProductGrowthLoop';

const tooltipStyle = {
    background: 'hsl(224 39% 15%)',
    border: '1px solid hsl(222 30% 22%)',
    borderRadius: 8,
    fontSize: 12,
};

function fmtPct(v: number | null): string {
    if (v == null) return 'n/d';
    return `${v.toLocaleString('es-ES', { maximumFractionDigits: 1 })}%`;
}

function fmtRatio(v: number | null): string {
    if (v == null) return 'n/d';
    return v.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

function DeltaLine({
    current,
    previous,
    unit = 'num',
}: {
    current: number | null;
    previous: number | null;
    unit?: 'pp' | 'num';
}) {
    const delta = unit === 'pp'
        ? pctPointDelta(current, previous)
        : (current == null || previous == null ? null : Number((current - previous).toFixed(2)));
    if (delta == null) return null;
    const good = delta >= 0;
    return (
        <span className={cn('text-[11px] font-semibold', good ? 'text-emerald-400' : 'text-rose-400')}>
            {delta > 0 ? '+' : ''}{delta}{unit === 'pp' ? ' pp' : ''} vs periodo ant.
        </span>
    );
}

function StatTile({
    icon,
    title,
    value,
    subtitle,
    delta,
}: {
    icon: ReactNode;
    title: string;
    value: string;
    subtitle: string;
    delta?: React.ReactNode;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 min-w-0">
            <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-muted-foreground">{title}</p>
                <span className="text-primary shrink-0">{icon}</span>
            </div>
            <p className="text-xl font-bold text-foreground mt-1 tabular-nums">{value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
            {delta ? <div className="mt-1">{delta}</div> : null}
        </div>
    );
}

function OverviewTiles({ o }: { o: ProductSessionOverview }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile
                icon={<Repeat2 size={16} />}
                title="Sesiones"
                value={formatNumber(o.sessions)}
                subtitle={`${formatNumber(o.users)} usuarios con sesión`}
                delta={<DeltaLine current={o.sessions} previous={o.sessions_previous} />}
            />
            <StatTile
                icon={<Users size={16} />}
                title="Sesiones por usuario"
                value={fmtRatio(o.sessions_per_user)}
                subtitle="Media en el periodo"
                delta={<DeltaLine current={o.sessions_per_user} previous={o.sessions_per_user_previous} />}
            />
            <StatTile
                icon={<Timer size={16} />}
                title="Duración mediana"
                value={formatDurationSec(o.duration_p50_sec)}
                subtitle="Lapso entre primer y último evento de la sesión"
                delta={<DeltaLine current={o.duration_p50_sec} previous={o.duration_p50_sec_previous} />}
            />
            <StatTile
                icon={<Camera size={16} />}
                title="Capturas por sesión"
                value={fmtRatio(o.captures_per_session)}
                subtitle={`${fmtPct(o.pct_with_capture)} de sesiones con al menos una · ${formatNumber(o.captures)} capturas`}
            />
        </div>
    );
}

function MilestoneCard({ m }: { m: ProductCaptureMilestone }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <p className="text-xs font-semibold text-foreground">{m.label}</p>
            <p className="text-2xl font-bold text-foreground tabular-nums">
                {formatHoursToHuman(m.hours_p50)}
            </p>
            <p className="text-[11px] text-muted-foreground">
                Mediana desde el registro · p90 {formatHoursToHuman(m.hours_p90)}
            </p>
            <p className="text-sm text-foreground tabular-nums">
                En la sesión {m.session_n_p50 ?? 'n/d'}
                {m.session_n_avg != null ? (
                    <span className="text-[11px] text-muted-foreground">
                        {' '}(media {m.session_n_avg.toLocaleString('es-ES')})
                    </span>
                ) : null}
            </p>
            <p className="text-[11px] text-muted-foreground">
                {fmtPct(m.reached_pct)} de los {formatNumber(m.cohort_registered)} registrados
                {' '}({formatNumber(m.users_reached)} llegaron)
            </p>
        </div>
    );
}

interface ProductSessionAnalyticsPanelProps {
    days: number;
}

export function ProductSessionAnalyticsPanel({ days }: ProductSessionAnalyticsPanelProps) {
    const { data, isLoading, isError, error, refetch, isFetching } = useProductSessionAnalytics(days);

    if (isLoading && !data) {
        return (
            <div className="rounded-xl border border-border bg-card p-8 flex flex-col items-center gap-2">
                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Calculando sesiones y capturas…</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-5 space-y-2">
                <p className="text-sm font-semibold text-rose-400">No se pudieron cargar las sesiones</p>
                <p className="text-xs text-muted-foreground">{(error as Error)?.message}</p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                    <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
                    Reintentar
                </button>
            </div>
        );
    }

    if (!data) return null;

    const durationChart = data.by_session.map((b) => ({
        label: b.key === '11+' ? '11+' : b.key,
        minutos: b.duration_p50_sec != null ? Number((b.duration_p50_sec / 60).toFixed(1)) : 0,
    }));

    const captureChart = data.by_session.map((b) => ({
        label: b.key === '11+' ? '11+' : b.key,
        capturas: b.captures_per_session ?? 0,
        conCaptura: b.pct_with_capture ?? 0,
    }));

    const milestones = [1, 3, 5].map((n) =>
        data.time_to_captures.find((m) => m.capture_n === n) ?? {
            capture_n: n,
            label: `${n}ª captura`,
            users_reached: 0,
            cohort_registered: data.time_to_captures[0]?.cohort_registered ?? 0,
            reached_pct: 0,
            hours_p50: null,
            hours_p90: null,
            session_n_p50: null,
            session_n_avg: null,
        }
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Sesiones y capturas</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                        Una sesión es una visita: agrupamos arranques de la app si hay menos de 30 min
                        entre ellos. El número es de por vida (sesión 1 = primera visita).
                        Duración = primer evento de esa visita hasta el último antes de la siguiente.
                        La captura cuenta en la visita que estaba abierta.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    title="Actualizar"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className="flex gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
                <Info size={14} className="shrink-0 text-primary mt-0.5" />
                <span>
                    Periodo de {days} días · {data.comparison_label}.
                    1ª/3ª/5ª captura se mide sobre quienes se registraron en este periodo.
                    Sesión = visita con hueco de 30 minutos.
                </span>
            </div>

            <OverviewTiles o={data.overview} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl border border-border bg-card p-4">
                    <h4 className="text-xs font-semibold text-foreground mb-1">Duración mediana por nº de sesión</h4>
                    <p className="text-[11px] text-muted-foreground mb-3">Minutos. Sesión 1 = primer arranque.</p>
                    <div className="h-[220px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={durationChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                <XAxis dataKey="label" tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    formatter={(value: number) => [`${value} min`, 'Mediana']}
                                />
                                <Bar dataKey="minutos" fill="hsl(199, 89%, 48%)" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                    <h4 className="text-xs font-semibold text-foreground mb-1">Capturas según el nº de sesión</h4>
                    <p className="text-[11px] text-muted-foreground mb-3">Media de capturas y % de sesiones con al menos una.</p>
                    <div className="h-[220px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={captureChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                <XAxis dataKey="label" tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis yAxisId="left" tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }} axisLine={false} tickLine={false} width={36} unit="%" />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    formatter={(value: number, name: string) => {
                                        if (name === 'capturas') return [value, 'Capturas / sesión'];
                                        return [`${value}%`, '% con captura'];
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar yAxisId="left" dataKey="capturas" name="Capturas / sesión" fill="hsl(172, 66%, 50%)" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                                <Bar yAxisId="right" dataKey="conCaptura" name="% con captura" fill="hsl(38, 92%, 50%)" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div>
                <h4 className="text-xs font-semibold text-foreground mb-1">Hasta la 1ª, 3ª y 5ª captura</h4>
                <p className="text-[11px] text-muted-foreground mb-3">
                    De los registrados en este periodo: cuánto tardan (mediana) y en qué sesión lo consiguen.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {milestones.map((m) => (
                        <MilestoneCard key={m.capture_n} m={m} />
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-muted-foreground border-b border-border">
                            <th className="text-left py-2 font-medium">Sesión nº</th>
                            <th className="text-right py-2 font-medium">Sesiones</th>
                            <th className="text-right py-2 font-medium">Duración p50</th>
                            <th className="text-right py-2 font-medium">Capturas</th>
                            <th className="text-right py-2 font-medium">Cap. / sesión</th>
                            <th className="text-right py-2 font-medium">% con captura</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.by_session.map((b) => (
                            <tr key={b.key} className="border-b border-border/40">
                                <td className="py-1.5">{b.label}</td>
                                <td className="py-1.5 text-right tabular-nums">{formatNumber(b.sessions)}</td>
                                <td className="py-1.5 text-right tabular-nums">{formatDurationSec(b.duration_p50_sec)}</td>
                                <td className="py-1.5 text-right tabular-nums">{formatNumber(b.captures)}</td>
                                <td className="py-1.5 text-right tabular-nums">{fmtRatio(b.captures_per_session)}</td>
                                <td className="py-1.5 text-right tabular-nums">{fmtPct(b.pct_with_capture)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
